import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Logo } from "../components/brand/Logo";
import { ReadAloud, ThemeToggle } from "../components/brand/Experience";

const STEPS = [
  { key: "roots", label: "Les racines", title: "Le savoir", body: "Des connaissances cliniques et des règles de rééducation transparentes nourrissent chaque séance, comme les racines nourrissent le baobab." },
  { key: "trunk", label: "Le tronc", title: "La plateforme", body: "Bombacare Connect relie le jeu Baobab Adventures et l'espace clinique Baobab Care. Solide, sûre, toujours disponible." },
  { key: "branches", label: "Les branches", title: "Les praticiens", body: "Kinésithérapeutes, ergothérapeutes et médecins configurent les séances, suivent les progrès et gardent la main sur la sécurité." },
  { key: "fruit", label: "Le fruit", title: "La guérison", body: "Chaque mouvement de Bao fait revivre le Grand Baobab… et accompagne pas à pas votre récupération." },
];

const draw = (on, delay = 0) => ({ initial: { pathLength: 0, opacity: 0 }, animate: on ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }, transition: { duration: 1.4, delay, ease: "easeInOut" } });
const glow = (active) => ({ filter: active ? "drop-shadow(0 0 10px rgba(224,169,46,.75))" : "none" });

const BaobabStory = ({ step }) => (
  <svg viewBox="0 0 400 430" className="h-full max-h-[52vh] w-full" role="img" aria-label={`Baobab : ${STEPS[step].label}`} data-testid="onboarding-baobab">
    <path d="M30 300 H370" stroke="#4FD1C5" strokeOpacity=".35" strokeWidth="2" strokeDasharray="4 8" />
    <g style={glow(step === 0)} stroke="#E0B33A" strokeWidth="5" strokeLinecap="round" fill="none">
      {["M185 300 C170 340 130 360 90 400", "M200 300 C200 345 195 380 200 420", "M215 300 C235 340 275 360 310 398", "M192 305 C160 320 120 322 70 340", "M208 305 C245 318 285 322 330 338"].map((d, i) => (
        <motion.path key={d} d={d} {...draw(step >= 0, i * 0.15)} />
      ))}
    </g>
    <motion.path d="M170 302 C176 250 174 200 166 160 L234 160 C226 200 224 250 230 302 Z" fill="#7A5236"
      initial={{ scaleY: 0, opacity: 0 }} animate={step >= 1 ? { scaleY: 1, opacity: 1 } : { scaleY: 0, opacity: 0 }}
      style={{ originY: 1, transformBox: "fill-box", ...glow(step === 1) }} transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1] }} />
    <g style={glow(step === 2)} stroke="#7A5236" strokeWidth="12" strokeLinecap="round" fill="none">
      {["M178 168 C150 130 110 115 70 110", "M195 162 C190 120 180 95 170 70", "M205 162 C212 120 222 95 232 70", "M222 168 C250 130 290 115 330 110", "M185 180 C150 175 120 170 95 160", "M215 180 C250 175 280 170 305 160"].map((d, i) => (
        <motion.path key={d} d={d} {...draw(step >= 2, i * 0.12)} />
      ))}
    </g>
    {[[70, 104], [170, 64], [232, 64], [330, 104], [95, 152], [305, 152]].map(([x, y], i) => (
      <g key={i}>
        <motion.circle cx={x} cy={y} r="30" fill="#17A2A2" fillOpacity=".8" initial={{ scale: 0 }} animate={{ scale: step >= 2 ? 1 : 0 }} transition={{ delay: 0.6 + i * 0.1, type: "spring", stiffness: 140 }} style={{ transformBox: "fill-box", originX: 0.5, originY: 0.5 }} />
        <motion.circle cx={x + 8} cy={y + 18} r="9" fill="#E0B33A" initial={{ scale: 0 }} animate={{ scale: step >= 3 ? 1 : 0 }} transition={{ delay: 0.3 + i * 0.15, type: "spring", stiffness: 220, damping: 10 }}
          style={{ transformBox: "fill-box", originX: 0.5, originY: 0.5, ...glow(step === 3) }} />
      </g>
    ))}
  </svg>
);

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const s = STEPS[step];
  const finish = useCallback(() => { localStorage.setItem("bc.onboarded", "1"); nav("/"); }, [nav]);
  const next = useCallback(() => (step < 3 ? setStep(step + 1) : finish()), [step, finish]);
  const prev = useCallback(() => setStep((x) => Math.max(0, x - 1)), []);
  useEffect(() => {
    const k = (e) => { if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); if (e.key === "Escape") finish(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [next, prev, finish]);

  return (
    <div className="bg-navy-depth grain relative flex min-h-[100dvh] flex-col overflow-hidden text-white" data-testid="onboarding">
      <div className="absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-[#17A2A2]/20 blur-3xl" />
      <header className="safe-top relative z-10 flex items-center justify-between px-5 pb-2 sm:px-10">
        <div className="flex items-center gap-3"><Logo size={40} /><span className="text-[11px] font-bold tracking-[0.32em] text-[#4FD1C5]">BOMBACARE CONNECT</span></div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button data-testid="onboarding-skip" onClick={finish} className="h-11 rounded-full px-4 text-sm font-semibold text-white/80 hover:text-white">Passer</button>
        </div>
      </header>
      <main className="relative z-10 mx-auto grid w-full max-w-6xl flex-1 items-center gap-6 px-5 sm:px-10 lg:grid-cols-2">
        <div className="flex items-center justify-center"><BaobabStory step={step} /></div>
        <div className="pb-6">
          <AnimatePresence mode="wait">
            <motion.div key={s.key} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.45 }} aria-live="polite">
              <p className="text-xs font-bold tracking-[0.35em] text-[#E0B33A]">{String(step + 1).padStart(2, "0")} · {s.label.toUpperCase()}</p>
              <h1 data-testid="onboarding-title" className="mt-3 font-display text-5xl font-semibold leading-[1.02] sm:text-6xl">{s.title}</h1>
              <p className="mt-5 max-w-lg text-lg leading-relaxed text-white/80">{s.body}</p>
              <div className="mt-6"><ReadAloud text={`${s.label}. ${s.title}. ${s.body}`} tone="dark" testId="onboarding-read-aloud" /></div>
            </motion.div>
          </AnimatePresence>
          <div className="mt-10 flex items-center gap-2" role="tablist" aria-label="Étapes">
            {STEPS.map((x, i) => (
              <button key={x.key} role="tab" aria-selected={i === step} aria-label={x.label} data-testid={`onboarding-dot-${i}`} onClick={() => setStep(i)}
                className={`hit-exempt h-2.5 rounded-full transition-all duration-500 ${i === step ? "w-10 bg-[#E0B33A]" : "w-2.5 bg-white/30 hover:bg-white/50"}`} />
            ))}
          </div>
          <div className="safe-bottom mt-8 flex gap-3">
            <button data-testid="onboarding-prev" onClick={prev} disabled={step === 0} aria-label="Étape précédente"
              className="flex h-14 w-14 items-center justify-center rounded-full border border-white/20 disabled:opacity-30"><ArrowLeft /></button>
            <button data-testid="onboarding-next" onClick={next}
              className="bg-brand flex h-14 flex-1 items-center justify-center gap-2 rounded-full px-6 text-base font-extrabold shadow-lg shadow-[#17A2A2]/30 sm:flex-none">
              {step < 3 ? "Continuer" : "Entrer dans Bombacare Connect"}<ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
